<template>
  <div class="s-date-range-picker d-flex justify-content-between flex-wrap w-100">
    <div class="w-48 d-flex flex-column justify-content-end">
      <SDatePicker
        v-model="locals.begins_at"
        :toggle-modal="toggle.begins_at"
        :max-date="maxDate"
        :min-date="minDate"
        :is-range-enforced="isRangeEnforced"
        :disabled="disabled || disableBeginsAt"
        hide-control
        data-cy="sdui-date-picker-begins_at"
        :validation-rules="`isSameOrBefore:${locals.ends_at}`"
        :validation-field-name="validationFieldNameBeginsAt"
        :validation-field-label="validationFieldLabelBeginsAt"
        @close="toggle.begins_at = false"
      >
        <STimePicker v-if="enableTimeSelect && !enableMinMaxDateControl"
                     v-model="locals.begins_at"
                     data-cy="time-picker-begins-at"
        />
        <SMinMaxDateControl v-if="enableMinMaxDateControl"
                            v-model="locals.begins_at"
                            :date-to-set="minDate"
                            data-cy="min-max-begins-at"
        />
      </SDatePicker>
      <span id="begins-at-range-datepicker"
            data-cy="from-title"
            class="mb-2 f-label d-inline-block"
      >{{ fromTitleDate }}</span>
      <div
        class="pl-1 border br-8 sdui-shadow"
        :class="{ 'py-1 max-h-48': !enableTimeSelect, 'py-2': enableTimeSelect, 's-date-range-picker--invalid': invalidTextBeginsAt }"
      >
        <!--eslint-disable-next-line sdui/require-focus-restore-on-disabled-button-->
        <b-button
          :aria-describedby="`begins-at-range-datepicker ${invalidTextBeginsAt ? 'begins-at-error' : ''}`"
          data-cy="date-range-picker-begins-at"
          variant="transparent"
          class="w-100 p-0 d-flex align-items-center min-w-125"
          :disabled="disabled || disableBeginsAt"
          @click="toggle.begins_at = !toggle.begins_at"
        >
          <sdui-icon variant="dark"
                     bg-variant="transparent"
                     class="pr-2"
                     :class="computedIconClass"
          />
          <span class="d-flex flex-column align-items-start limited-font-size">
            <time class="font-weight-bold"
                  data-cy="begins-at-date"
                  :datetime="$dateFormat.timestamp(locals.begins_at)"
            >{{
              beginsAtButtonCaption
            }}</time>
            <span v-if="enableTimeSelect"
                  class="font-weight-normal text-left"
                  data-cy="begins-at-start-of-day"
            >{{
              calculateTimeToShow($dateFormat.hour_minute(locals.begins_at))
            }}</span>
          </span>
        </b-button>
      </div>
    </div>
    <div class="w-48 d-flex flex-column justify-content-end">
      <SDatePicker
        v-model="locals.ends_at"
        :toggle-modal="toggle.ends_at"
        :target="`endsAtBtn:${locals.begins_at}`"
        :max-date="maxDate"
        :min-date="minDate"
        :is-range-enforced="isRangeEnforced"
        data-cy="sdui-date-picker-ends_at"
        :disabled="disabled || disableEndsAt"
        hide-control
        :validation-rules="`isSameOrAfter:${locals.begins_at}`"
        :validation-field-name="validationFieldNameEndsAt"
        :validation-field-label="validationFieldLabelEndsAt"
        @close="toggle.ends_at = false"
      >
        <STimePicker
          v-if="enableTimeSelect && !enableMinMaxDateControl"
          v-model="locals.ends_at"
          :begin_at="false"
          ends_at
          data-cy="time-picker-ends-at"
        />
        <SMinMaxDateControl
          v-if="enableMinMaxDateControl"
          v-model="locals.ends_at"
          :is-begins-at="false"
          :date-to-set="maxDate"
          data-cy="min-max-ends-at"
        />
      </SDatePicker>
      <span id="ends-at-range-datepicker"
            data-cy="to-title"
            class="mb-2 f-label d-inline-block"
      >{{ toTitleDate }}</span>
      <div
        class="pl-1 border br-8 sdui-shadow"
        :class="{ 'py-1 max-h-48': !enableTimeSelect, 'py-2': enableTimeSelect, 's-date-range-picker--invalid': invalidTextEndsAt }"
      >
        <!--eslint-disable-next-line sdui/require-focus-restore-on-disabled-button-->
        <b-button
          :id="`endsAtBtn:${locals.begins_at}`"
          :aria-describedby="`ends-at-range-datepicker ${invalidTextEndsAt ? 'ends-at-error' : ''}`"
          data-cy="date-range-picker-ends-at"
          variant="transparent"
          class="w-100 p-0 d-flex align-items-center min-w-125"
          :disabled="disabled || disableEndsAt"
          @click="toggle.ends_at = !toggle.ends_at"
        >
          <sdui-icon variant="dark"
                     class="pr-2"
                     :class="computedIconClass"
                     bg-variant="transparent"
          />
          <span class="d-flex flex-column align-items-start limited-font-size">
            <time class="font-weight-bold"
                  data-cy="ends-at-date"
                  :datetime="$dateFormat.timestamp(locals.ends_at)"
            >{{
              endsAtButtonCaption
            }}</time>
            <span v-if="enableTimeSelect"
                  class="font-weight-normal text-left"
                  data-cy="ends-at-end-of-day"
            >{{
              calculateTimeToShow($dateFormat.hour_minute(locals.ends_at))
            }}</span>
          </span>
        </b-button>
      </div>
    </div>

    <div v-if="invalidTextBeginsAt || invalidTextEndsAt" class="w-100 mt-1">
      <span
        v-if="invalidTextBeginsAt"
        id="begins-at-error"
        class="text-danger-dark nano d-inline-block mb-0"
        data-cy="begins-at-error-field"
      >
        {{ invalidTextBeginsAt }}
      </span>

      <span
        v-if="invalidTextEndsAt"
        id="ends-at-error"
        class="text-danger-dark nano d-inline-block mt-1 mb-0"
        data-cy="ends-at-error-field"
      >
        {{ invalidTextEndsAt }}
      </span>
    </div>
  </div>
</template>

<script>
import SDatePicker from 'src/components/base/datepicker/SDatePicker.vue'
import STimePicker from 'src/components/base/datepicker/STimePicker.vue'
import SMinMaxDateControl from 'src/components/base/datepicker/SMinMaxDateControl.vue'
import { useFieldError } from 'vee-validate'
import { onMounted, ref, watch } from 'vue'

export default {
  // TODO VUE3: This is a mix of Options API and Composition API which should be removed by refactoring
  // the whole component to Composition API. Right now it's needed to access validation state of VeeValidate.
  setup(props) {
    const validationErrorMessageBeginsAt = ref(undefined)
    const validationErrorMessageEndsAt = ref(undefined)

    onMounted(() => {
      const hasFieldBeginsAtError = useFieldError(props.validationFieldNameBeginsAt)
      const hasFieldEndsAtError = useFieldError(props.validationFieldNameEndsAt)

      watch(hasFieldBeginsAtError, (newVal) => {
        validationErrorMessageBeginsAt.value = newVal
      })
      watch(hasFieldEndsAtError, (newVal) => {
        validationErrorMessageEndsAt.value = newVal
      })
    })

    return {
      invalidTextBeginsAt: validationErrorMessageBeginsAt,
      invalidTextEndsAt: validationErrorMessageEndsAt,
    }
  },

  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  name: 'SDateRangePicker',
  components: {
    STimePicker,
    SDatePicker,
    SMinMaxDateControl,
  },

  props: {
    fromTitle: {
      type: String,
      default: null,
    },

    toTitle: {
      type: String,
      default: null,
    },

    minDate: {
      type: String,
      default: null,
    },

    minDateLabel: {
      type: String,
      default: null,
    },

    maxDate: {
      type: String,
      default: null,
    },

    maxDateLabel: {
      type: String,
      default: null,
    },

    // When true, forces out-of-date ranges to be snapped to the min and max dates. When false, display date passed to it as-is.
    isRangeEnforced: {
      type: Boolean,
      default: true,
    },

    modelValue: {
      type: Object,
      default: () => ({
        begins_at: '',
        ends_at: '',
      }),
    },

    enableTimeSelect: {
      type: Boolean,
      default: true,
    },

    enableMinMaxDateControl: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    disableBeginsAt: {
      type: Boolean,
      default: false,
    },

    disableEndsAt: {
      type: Boolean,
      default: false,
    },

    validationFieldNameBeginsAt: {
      type: String,
      default: 'begins_at',
    },

    validationFieldNameEndsAt: {
      type: String,
      default: 'ends_at',
    },

    validationFieldLabelBeginsAt: {
      type: String,
      default: 'begins_at',
    },

    validationFieldLabelEndsAt: {
      type: String,
      default: 'ends_at',
    },
  },

  data: () => ({
    locals: {
      begins_at: '',
      ends_at: '',
    },

    toggle: {
      begins_at: false,
      ends_at: false,
    },
  }),

  computed: {
    fromTitleDate() {
      return this.fromTitle || this.$t('management.periods.begins_at')
    },

    toTitleDate() {
      return this.toTitle || this.$t('management.periods.ends_at')
    },

    computedIconClass() {
      return this.enableTimeSelect ? 'icon-clock-o' : 'icon-clock-o-16'
    },

    beginsAtButtonCaption() {
      if (!this.minDateLabel || !this.$moment(this.minDate).isSame(this.locals.begins_at))
        return this.$dateFormat.date_yearlong(this.locals.begins_at)
      return this.minDateLabel
    },

    endsAtButtonCaption() {
      if (!this.maxDateLabel || !this.$moment(this.maxDate).isSame(this.locals.ends_at))
        return this.$dateFormat.date_yearlong(this.locals.ends_at)
      return this.maxDateLabel
    },
  },

  emits: ['update:modelValue'],

  methods: {
    validEndDate(date) {
      if (!this.minDate) return date

      return (!this.isRangeEnforced && this.$moment(date).isValid()) || this.$moment(date).isSameOrAfter(this.minDate)
        ? this.$dateFormat.timestamp(date)
        : this.$dateFormat.timestamp(this.$moment(this.minDate).endOf('day'))
    },

    calculateTimeToShow(time) {
      if (time !== '00:00' && time !== '23:59') return time
      if (time === '00:00') return this.$t('ui.date_time_picker.start_of_day')
      return this.$t('ui.date_time_picker.end_of_day')
    },
  },

  watch: {
    'locals.begins_at': {
      handler(newDate, oldDate) {
        if (!newDate) return
        // check if equal to avoid infinite loop
        if (newDate && oldDate && this.$moment(newDate).isSame(this.$moment(oldDate))) return

        this.$emit('update:modelValue', {
          ...this.modelValue,
          begins_at: newDate,
          ends_at: this.locals.ends_at,
        })
      },

      immediate: true,
    },

    'locals.ends_at': {
      handler(newDate, oldDate) {
        if (!newDate) return
        // check if equal to avoid infinite loop
        if (newDate && oldDate && this.$moment(newDate).isSame(this.$moment(oldDate))) return

        this.$emit('update:modelValue', {
          ...this.modelValue,
          begins_at: this.locals.begins_at,
          ends_at: newDate,
        })
      },

      immediate: true,
    },

    modelValue: {
      handler(val) {
        if (!val) return
        this.locals = {
          begins_at: val.begins_at || this.$dateFormat.timestamp(),
          ends_at: this.validEndDate(val.ends_at) || this.validEndDate(this.$moment().add(1, 'minutes')),
        }
      },

      immediate: true,
      deep: true,
    },
  },
}
</script>

<style scoped lang="scss">
.s-date-range-picker--invalid {
  border-color: $color-danger-dark !important;

  time,
  span {
    color: $color-danger-dark !important;
  }
}

.w-48 {
  width: 48%;
}

.min-w-125 {
  min-width: 125px;
}

.max-h-48 {
  max-height: 48px;
}

.limited-font-size {
  /* stylelint-disable-next-line declaration-property-value-allowed-list */
  font-size: min(1rem, 25px);
}
</style>
